import { useEffect, useMemo, useState } from "react";
import { api, ApiError, type HistoricoItem } from "../api";
import { formatDate } from "../format";
import ErrorState from "./ErrorState";
import LaudoDetalhe from "./LaudoDetalhe";
import { IconSearch, IconDownload } from "../icons";

/** Cor da pílula conforme o resultado da análise. */
const RESULTADO_PILL: Record<string, string> = {
  Aprovado: "pill-ok",
  Reprovado: "pill-danger",
  "Com ressalvas": "pill-warn",
};

/** Rótulo fixo do tipo de análise (o Laboratório só faz Análise de Óleo). */
const MODELO_ANALISE = "Análise de Óleo";

/**
 * Aba HISTORICO — análises de óleo DESPACHADAS pelo Laboratório.
 * Colunas: OS · Cliente · Modelo da análise · Recebido · Despachado ·
 * Tipo de equipamento · Resultado · Laudo (baixar o .docx gerado).
 */
export default function Historico() {
  const [itens, setItens] = useState<HistoricoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [search, setSearch] = useState("");
  const [baixando, setBaixando] = useState<number | null>(null);
  /** Laudo aberto na tela de visualização (dois cliques na linha). */
  const [aberto, setAberto] = useState<HistoricoItem | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await api.historico.listar();
      setItens(r.historico ?? []);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtrados = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return itens;
    return itens.filter((i) =>
      [i.os_numero, i.cliente, i.equipamento, i.resultado, MODELO_ANALISE].some((v) =>
        String(v ?? "").toLowerCase().includes(q),
      ),
    );
  }, [itens, search]);

  async function baixar(item: HistoricoItem) {
    if (baixando) return;
    setBaixando(item.id);
    try {
      await api.historico.baixarDocumento(item.id, item.laudo_nome || `analise-oleo-OS-${item.os_numero}.docx`);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao baixar o laudo");
    } finally {
      setBaixando(null);
    }
  }

  if (error) return <ErrorState message={error.message} code={error.code} onRetry={load} />;

  return (
    <div className="crud">
      <div className="crud-toolbar">
        <div className="search-wrap">
          <IconSearch size={17} />
          <input
            className="search"
            placeholder="Buscar OS, cliente, equipamento ou resultado..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="toolbar-right">
          <span className="muted hist-dica">Dois cliques na linha para ver o laudo inteiro</span>
          <span className="badge">{filtrados.length} análise(s) despachada(s)</span>
        </div>
      </div>

      <div className="table-wrap">
        {loading ? (
          <div className="empty">Carregando...</div>
        ) : filtrados.length === 0 ? (
          <div className="empty">
            {search
              ? "Nenhum resultado para a busca."
              : "Nenhuma análise despachada ainda — ao despachar uma análise ao Relatórios, ela aparece aqui."}
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>OS</th>
                <th>Cliente</th>
                <th>Modelo da análise</th>
                <th>Recebido</th>
                <th>Despachado</th>
                <th>Tipo de equipamento</th>
                <th>Resultado</th>
                <th>Laudo</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((item) => (
                // Dois cliques abrem o laudo inteiro — o mesmo gesto que já
                // abre a análise e a conferência da remessa nas outras abas.
                <tr
                  key={item.id}
                  className="row-clickable"
                  onDoubleClick={() => setAberto(item)}
                  title="Dois cliques para ver o laudo inteiro"
                >
                  <td>
                    <strong>{item.os_numero}</strong>
                  </td>
                  <td>{item.cliente || "—"}</td>
                  <td>{MODELO_ANALISE}</td>
                  <td>{formatDate(item.data_recebimento)}</td>
                  <td>{formatDate(item.despachado_em)}</td>
                  <td>{item.equipamento || "—"}</td>
                  <td>
                    {item.resultado ? (
                      <span className={`pill ${RESULTADO_PILL[item.resultado] ?? "pill-neutral"}`}>
                        {item.resultado}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    {item.tem_laudo ? (
                      <button
                        type="button"
                        className="hist-doc-btn"
                        title={`Baixar ${item.laudo_nome || "laudo"}`}
                        disabled={baixando !== null}
                        // Sem isto, o clique no botão contaria como parte do
                        // duplo clique da linha e abriria o laudo junto.
                        onDoubleClick={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          baixar(item);
                        }}
                      >
                        <IconDownload size={13} />
                        {baixando === item.id ? "Baixando..." : "Baixar"}
                      </button>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {aberto && (
        <LaudoDetalhe
          item={aberto}
          onCancel={() => setAberto(null)}
          onBaixar={(item) => void baixar(item)}
        />
      )}
    </div>
  );
}
